import type { Card } from "../src/engine/cards.ts";
import type { GameEvent, TableConfig } from "../src/engine/game.ts";
import type { Cents } from "../src/engine/money.ts";
import type { ClientMsg, PlayerView, ServerMsg, TableView } from "../src/shared/protocol.ts";
import { AVATAR_COLORS, CARD_BACK_SVG, DEALER_BACK_SVG, DEALER_FRONT_SVG, FELT_NOISE_SVG, SHARED_DEFS, avatarColor, cardFaceSVG } from "./art.ts";
import { chipStackHTML, money, moneyShort } from "./chips.ts";
import type { Identity } from "./identity.ts";
import type { Transport } from "./transport.ts";

const W = 1280;
const H = 720;
const N = 6;

/**
 * Where each visual slot sits on the 1280x720 stage, clockwise from the viewer.
 * Six seats form a flat-topped hexagon so the dealer has the top-centre of the table to themselves.
 * Slot 0 is always the viewer (bottom-left).
 */
const SLOTS = [
  { x: 350, y: 560, bet: [488, 466], btn: [236, 540] },
  { x: 140, y: 372, bet: [280, 372], btn: [252, 326] },
  { x: 330, y: 178, bet: [418, 262], btn: [436, 128] },
  { x: 950, y: 178, bet: [862, 262], btn: [844, 128] },
  { x: 1140, y: 372, bet: [1000, 372], btn: [1028, 326] },
  { x: 930, y: 560, bet: [792, 466], btn: [1044, 540] },
] as const;

const POT_POS = { x: 640, y: 262 };
const DEALER_HANDS = [{ x: 594, y: 153 }, { x: 686, y: 153 }];

type UpdateMsg = Extract<ServerMsg, { t: "update" }>;

interface SeatEls {
  root: HTMLElement;
  cards: HTMLElement[];
  avatar: HTMLElement;
  initial: HTMLElement;
  name: HTMLElement;
  stack: HTMLElement;
  badge: HTMLElement;
  prog: SVGCircleElement;
  bet: HTMLElement;
}

const el = <T extends HTMLElement = HTMLElement>(cls: string, html = "", tag = "div"): T => {
  const e = document.createElement(tag) as T;
  e.className = cls;
  if (html) e.innerHTML = html;
  return e;
};

function makeCard(): HTMLElement {
  return el("card", `<div class="inner"><div class="back">${CARD_BACK_SVG}</div><div class="face"></div></div>`);
}

function setFace(card: HTMLElement, value: Card): void {
  const face = card.querySelector<HTMLElement>(".face")!;
  if (face.dataset.card !== String(value)) {
    face.innerHTML = cardFaceSVG(value);
    face.dataset.card = String(value);
  }
}

export class App {
  private stage: HTMLElement;
  private scale = 1;
  private seats: SeatEls[] = [];
  private boardCards: HTMLElement[] = [];
  private potEl!: HTMLElement;
  private dbtn!: HTMLElement;
  private dealerEl!: HTMLElement;
  private dealerBack!: HTMLElement;
  private speechEl!: HTMLElement;
  private bar!: HTMLElement;
  private toastEl!: HTMLElement;
  private banner!: HTMLElement;
  private modal!: HTMLElement;
  private corner!: HTMLElement;
  private blinds!: HTMLElement;

  private view: TableView | null = null;
  private config: TableConfig | null = null;
  private allowBots = false;
  private connected = true;

  private names = new Map<string, string>();
  private seatById = new Map<string, number>();
  private holeShown: boolean[][] = Array.from({ length: N }, () => [false, false]);
  private boardShown = 0;
  private revealHold = false;
  private lastAction = new Map<number, { text: string; kind: string }>();
  private winners = new Map<number, { amount: Cents; hand?: string }>();
  private handLabels = new Map<number, string>();
  private prevBets: Cents[] = new Array(N).fill(0);
  private prevBetHTML: string[] = new Array(N).fill("");
  private lastPotHTML = "";

  private queue: Array<{ msg: UpdateMsg; at: number }> = [];
  private busy = false;
  private speakTimer = 0;
  private flickSide = 0;
  private turnTimers: number[] = [];
  private ringAnim: Animation | null = null;
  private raiseTo = 0;
  private raiseKey = "";
  private reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  private toastTimer = 0;
  /** True while a batch of events is being played; controls stay hidden so stale buttons can't be clicked. */
  private animating = false;

  constructor(
    private identity: Identity,
    private transport: Transport,
    private opts: { autoSit?: boolean; blindsLabel?: string } = {},
  ) {
    this.stage = document.getElementById("stage")!;
    this.build();
    this.fit();
    addEventListener("resize", () => this.fit());

    transport.onOpen(() => transport.send({ t: "join", playerId: identity.playerId, name: identity.name, avatar: identity.avatar, token: identity.token }));
    transport.onStatus((c) => {
      this.connected = c;
      this.banner.classList.toggle("on", !c);
      this.renderBar();
    });
    transport.onMessage((m) => this.onMessage(m));
  }

  // ===================================================================== build

  private build(): void {
    const s = this.stage;
    s.innerHTML = SHARED_DEFS;
    // Back to front: lamp shade, dealer (behind the table), table, light, brand
    this.dealerBack = el("dealer dealer-back", DEALER_BACK_SVG);
    s.append(el("lamp-shade"), this.dealerBack);
    s.append(el("table-rim"), el("table-cushion"), el("table-felt", FELT_NOISE_SVG));
    s.append(el("felt-mark", "www.wiseguys.ltd"), el("lamp"), el("smoke", "<i></i><i></i><i></i>"));
    s.append(el("brand", `<b>Wiseguys Hold 'Em</b><span class="blinds"></span>`));
    this.blinds = s.querySelector(".blinds")!;

    this.potEl = el("pot empty", `<div class="pot-chips"></div><div class="pot-label money"></div>`);
    s.append(this.potEl);

    const board = el("board");
    for (let i = 0; i < 5; i++) {
      const wrap = el("slot-wrap", `<div class="slot"></div>`);
      const c = makeCard();
      wrap.append(c);
      board.append(wrap);
      this.boardCards.push(c);
    }
    s.append(board);

    this.dealerEl = el("dealer dealer-front", DEALER_FRONT_SVG);
    this.speechEl = el("speech");
    s.append(this.dealerEl, this.speechEl);

    for (let i = 0; i < N; i++) s.append(this.buildSeat(i));
    for (let i = 0; i < N; i++) {
      const bet = el("bet");
      this.seats[i]!.bet = bet;
      s.append(bet);
    }
    this.dbtn = el("dbtn", "D");
    this.dbtn.style.opacity = "0";
    s.append(this.dbtn);

    this.bar = el("bar");
    this.toastEl = el("toast");
    this.banner = el("banner", "Reconnecting to the table…");
    this.corner = el("corner");
    this.modal = el("modal-bg");
    s.append(this.bar, this.toastEl, this.banner, this.corner, this.modal);
  }

  private buildSeat(i: number): HTMLElement {
    const root = el("seat empty");
    root.dataset.seat = String(i);
    root.innerHTML =
      `<div class="cards"></div>` +
      `<div class="plate"><div class="avatar"><span class="ini"></span>` +
      `<svg class="ring" viewBox="0 0 62 62"><circle class="track" cx="31" cy="31" r="29"/><circle class="prog" cx="31" cy="31" r="29"/></svg>` +
      `<i class="offline"></i></div>` +
      `<div class="who"><div class="name"></div><div class="stack-amt money"></div></div></div>` +
      `<div class="badge"></div><button class="sit">Sit here</button>`;
    const cardsBox = root.querySelector(".cards")!;
    const cards = [makeCard(), makeCard()];
    cardsBox.append(...cards);
    root.querySelector<HTMLButtonElement>(".sit")!.onclick = () => this.openBuyIn(i);
    this.seats[i] = {
      root,
      cards,
      avatar: root.querySelector(".avatar")!,
      initial: root.querySelector(".ini")!,
      name: root.querySelector(".name")!,
      stack: root.querySelector(".stack-amt")!,
      badge: root.querySelector(".badge")!,
      prog: root.querySelector<SVGCircleElement>(".prog")!,
      bet: null as unknown as HTMLElement,
    };
    return root;
  }

  private fit(): void {
    this.scale = Math.min(innerWidth / W, innerHeight / H);
    const x = (innerWidth - W * this.scale) / 2;
    const y = (innerHeight - H * this.scale) / 2;
    this.stage.style.transform = `translate(${x}px, ${y}px) scale(${this.scale})`;
  }

  /** Centre of an element in stage coordinates (undoes the page scale). */
  private pos(e: Element): { x: number; y: number } {
    const r = e.getBoundingClientRect();
    const s = this.stage.getBoundingClientRect();
    return { x: (r.left + r.width / 2 - s.left) / this.scale, y: (r.top + r.height / 2 - s.top) / this.scale };
  }

  // =============================================================== messaging

  private send(msg: ClientMsg): void {
    this.transport.send(msg);
  }

  private onMessage(m: ServerMsg): void {
    if (m.t === "welcome") {
      this.config = m.config;
      this.allowBots = m.allowBots;
      this.updateBrand();
      if (this.opts.autoSit) {
        this.opts.autoSit = false;
        this.send({ t: "sit", buyIn: m.config.maxBuyIn });
      }
    } else if (m.t === "error") {
      this.toast(m.message);
    } else {
      this.queue.push({ msg: m, at: performance.now() });
      void this.pump();
    }
  }

  private async pump(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      while (this.queue.length) {
        const item = this.queue.shift()!;
        // If we're far behind (tab was in the background, etc.) skip the fancy animation.
        await this.process(item.msg, item.at, this.queue.length >= 2 || this.reduced || document.hidden);
      }
    } finally {
      this.busy = false;
    }
  }

  private updateBrand(): void {
    const c = this.config;
    if (!c) return;
    if (this.opts.blindsLabel) {
      this.blinds.textContent = this.opts.blindsLabel;
      return;
    }
    const locked = this.view?.started ? "" : " (not started)";
    this.blinds.textContent = `Blinds ${money(c.smallBlind)} / ${money(c.bigBlind)}, buy-in ${money(c.maxBuyIn)}${locked}`;
  }

  private async process(m: UpdateMsg, at: number, fast: boolean): Promise<void> {
    this.config = m.config; // the host can change the buy-in at any time
    this.updateBrand();
    const first = this.view === null;
    if (first) this.initFromView(m.view);

    const events = m.events;
    if (events.some((e) => e.type === "hand_started")) this.resetHand();
    this.revealHold = events.some((e) => e.type === "showdown");

    // Hide the previous turn's controls straight away, before any animation plays.
    this.animating = events.length > 0;
    this.clearTurn();
    this.renderBar();

    this.applyStatic(m.view);
    this.corner.innerHTML = "";
    this.renderCorner();

    try {
      for (const ev of events) await this.playEvent(ev, m.view, fast);
    } finally {
      this.animating = false;
    }
    this.revealHold = false;
    this.renderCards();
    this.renderBadges();

    const left = m.turnMsLeft === null ? null : Math.max(0, m.turnMsLeft - (performance.now() - at));
    this.applyTurn(m.view, left);
  }

  /** First snapshot of a table that's already in progress: show everything immediately. */
  private initFromView(v: TableView): void {
    this.holeShown = Array.from({ length: N }, (_, s) => [!!v.players[s]?.hole.length, !!v.players[s]?.hole.length]);
    this.boardShown = v.board.length;
  }

  private resetHand(): void {
    this.holeShown = Array.from({ length: N }, () => [false, false]);
    this.boardShown = 0;
    this.lastAction.clear();
    this.winners.clear();
    this.handLabels.clear();
  }

  // ============================================================ static render

  private applyStatic(v: TableView): void {
    this.view = v;
    const mine = v.players.find((p) => p?.id === this.identity.playerId) ?? null;
    const mySeat = mine ? mine.seat : 0;
    this.seatById.clear();
    for (const p of v.players) {
      if (!p) continue;
      this.seatById.set(p.id, p.seat);
      this.names.set(p.id, p.name);
    }

    let betsTotal = 0;
    for (let i = 0; i < N; i++) {
      const p = v.players[i] ?? null;
      const slot = SLOTS[(i - mySeat + N) % N]!;
      const e = this.seats[i]!;
      e.root.style.left = `${slot.x}px`;
      e.root.style.top = `${slot.y}px`;
      e.bet.style.left = `${slot.bet[0]}px`;
      e.bet.style.top = `${slot.bet[1]}px`;

      e.root.classList.toggle("empty", !p);
      e.root.classList.toggle("locked", !p && !!mine);
      e.root.classList.toggle("me", !!p && p.id === this.identity.playerId);
      e.root.classList.toggle("folded", !!p && p.folded);
      e.root.classList.toggle("out", !!p && p.sittingOut);
      e.root.classList.toggle("offline", !!p && !p.connected);
      if (p) {
        e.name.textContent = p.name;
        e.stack.textContent = money(p.stack);
        e.avatar.style.setProperty("--av", avatarColor(p.id));
        this.setAvatar(e, p);
      }

      // Bets: show chips in front of the player; when a bet disappears, sweep it into the pot.
      const bet = v.street === "waiting" ? 0 : (p?.bet ?? 0);
      betsTotal += bet;
      if (bet > 0) {
        const html = `${chipStackHTML(bet, 2, 5)}<div class="amt money">${moneyShort(bet)}</div>`;
        if (e.bet.innerHTML !== html) e.bet.innerHTML = html;
        this.prevBetHTML[i] = html;
      } else {
        if (this.prevBets[i]! > 0) this.sweep(e.bet, this.prevBetHTML[i]!);
        e.bet.innerHTML = "";
      }
      this.prevBets[i] = bet;
    }

    // Pot in the middle (bets already collected)
    const pot = v.street === "waiting" ? 0 : Math.max(0, v.pot - betsTotal);
    this.potEl.classList.toggle("empty", pot === 0);
    if (pot > 0) {
      this.potEl.querySelector(".pot-chips")!.innerHTML = chipStackHTML(pot, 3, 6);
      this.potEl.querySelector(".pot-label")!.innerHTML = `<small>Pot</small>${money(pot)}`;
      this.lastPotHTML = this.potEl.querySelector(".pot-chips")!.innerHTML;
    }

    // Dealer button
    if (v.handNumber > 0 && v.button >= 0) {
      const slot = SLOTS[(v.button - mySeat + N) % N]!;
      this.dbtn.style.left = `${slot.btn[0]}px`;
      this.dbtn.style.top = `${slot.btn[1]}px`;
      this.dbtn.style.opacity = "1";
    } else {
      this.dbtn.style.opacity = "0";
    }

    this.renderCards();
    this.renderBoard();
    this.renderBadges();
    this.updateBrand();
  }

  private setAvatar(e: SeatEls, p: PlayerView): void {
    const initial = (p.name.trim()[0] ?? "?").toUpperCase();
    // A picture that already failed to load stays as a letter instead of retrying on every update.
    if (p.avatar && e.avatar.dataset.failed !== p.avatar) {
      if (e.avatar.dataset.img !== p.avatar) {
        const img = document.createElement("img");
        img.alt = "";
        img.onerror = () => {
          e.avatar.dataset.failed = p.avatar;
          delete e.avatar.dataset.img;
          e.initial.textContent = initial;
        };
        img.src = p.avatar;
        e.initial.replaceChildren(img);
        e.avatar.dataset.img = p.avatar;
      }
    } else {
      delete e.avatar.dataset.img;
      e.initial.textContent = initial;
    }
  }

  private renderCards(): void {
    const v = this.view;
    if (!v) return;
    for (let s = 0; s < N; s++) {
      const p = v.players[s] ?? null;
      const show = !!p && p.hole.length === 2 && !p.folded;
      let anyUp = false;
      for (let i = 0; i < 2; i++) {
        const c = this.seats[s]!.cards[i]!;
        const dealt = show && this.holeShown[s]![i]!;
        const val = p?.hole[i] ?? -1;
        c.classList.toggle("dealt", dealt);
        if (dealt && val >= 0) setFace(c, val);
        const up = dealt && val >= 0 && (!this.revealHold || p!.id === this.identity.playerId);
        c.classList.toggle("up", up);
        anyUp = anyUp || up;
      }
      this.seats[s]!.root.querySelector(".cards")!.classList.toggle("reveal", anyUp && p!.id !== this.identity.playerId);
    }
  }

  private renderBoard(): void {
    const v = this.view;
    if (!v) return;
    this.boardCards.forEach((c, i) => {
      const shown = i < this.boardShown && i < v.board.length;
      c.classList.toggle("dealt", shown);
      if (shown) setFace(c, v.board[i]!);
      c.classList.toggle("up", shown);
    });
  }

  private renderBadges(): void {
    const v = this.view;
    if (!v) return;
    for (let s = 0; s < N; s++) {
      const p = v.players[s] ?? null;
      const b = this.seats[s]!.badge;
      let text = "";
      let kind = "";
      const win = this.winners.get(s);
      const act = this.lastAction.get(s);
      if (!p) text = "";
      else if (win) { text = `Wins ${money(win.amount)}${win.hand ? ` with ${win.hand}` : ""}`; kind = "win"; }
      else if (this.handLabels.has(s) && !this.revealHold) { text = this.handLabels.get(s)!; kind = "act"; }
      else if (p.folded) { text = "Fold"; kind = "fold"; }
      else if (p.allIn && v.street !== "waiting") { text = "All-in"; kind = "allin"; }
      else if (act) { text = act.text; kind = act.kind; }
      else if (p.sittingOut) { text = p.stack === 0 ? "Out of chips" : "Joining next hand"; kind = "fold"; }
      b.textContent = text;
      b.className = `badge ${kind}${text ? " on" : ""}`;
      this.seats[s]!.root.classList.toggle("winner", !!win);
    }
  }

  // ================================================================ turn state

  private clearTurn(): void {
    for (const t of this.turnTimers) clearTimeout(t);
    this.turnTimers = [];
    this.ringAnim?.cancel();
    this.ringAnim = null;
    for (const s of this.seats) s.root.classList.remove("turn", "urgent", "thinking");
  }

  private applyTurn(v: TableView, msLeft: number | null): void {
    this.clearTurn();

    if (v.toAct !== null && v.street !== "waiting") {
      const s = this.seats[v.toAct]!;
      s.root.classList.add("turn");
      if (msLeft === null) {
        s.root.classList.add("thinking");
      } else {
        this.ringAnim = s.prog.animate([{ strokeDashoffset: 0 }, { strokeDashoffset: 182 }], { duration: Math.max(1, msLeft), easing: "linear", fill: "forwards" });
        if (msLeft > 6000) this.turnTimers.push(window.setTimeout(() => s.root.classList.add("urgent"), msLeft - 6000));
        else s.root.classList.add("urgent");
      }
    }
    this.renderBar();
  }

  private mine(): PlayerView | null {
    return this.view?.players.find((p) => p?.id === this.identity.playerId) ?? null;
  }

  private renderBar(): void {
    const v = this.view;
    this.bar.innerHTML = "";
    if (!v || this.animating) return;
    const status = (t: string) => this.bar.append(el("status", t));
    const mine = this.mine();

    if (!this.connected) return status("Connection lost. Trying to reconnect…");
    if (!v.started) return this.renderLobby(v, mine, status);
    if (!mine) return status("Take an open seat to join the game.");

    if (v.legal && v.toAct === mine.seat) return this.renderControls(v, mine);

    if (mine.sittingOut && mine.stack === 0) {
      status("You're out of chips.");
      const b = el<HTMLButtonElement>("btn solid", `Rebuy ${money(this.config?.maxBuyIn ?? 0)}`, "button");
      b.onclick = () => this.send({ t: "rebuy", amount: this.config!.maxBuyIn });
      this.bar.append(b);
      return;
    }
    if (mine.sittingOut) return status("You'll be dealt in on the next hand.");
    if (mine.folded) return status("You folded. Watch the rest of the hand.");
    if (v.street === "waiting") {
      const seated = v.players.filter((p) => p && (p.stack > 0 || p.isBot)).length;
      return status(seated < 2 ? "Waiting for another player to sit down…" : "Next hand starting…");
    }
    if (v.toAct !== null) {
      const who = v.players[v.toAct];
      return status(`${who?.name ?? "Someone"} is thinking…`);
    }
  }

  /** Before the game starts: the host sets the stakes and deals everyone in; everyone else waits. */
  private renderLobby(v: TableView, mine: PlayerView | null, status: (t: string) => void): void {
    const seated = v.players.filter((p) => p && p.stack > 0).length;
    if (v.hostId === this.identity.playerId) {
      status(seated < 2 ? "Seat at least two players, then start the game." : `${seated} at the table. Set the stakes, then start.`);
      const settings = el<HTMLButtonElement>("btn felt", "Table settings", "button");
      settings.onclick = () => this.openSettings();
      const start = el<HTMLButtonElement>("btn solid start", "Start game", "button");
      start.disabled = seated < 2;
      start.onclick = () => this.send({ t: "startGame" });
      this.bar.append(settings, start);
      return;
    }
    status(mine ? "Waiting for the host to start the game…" : "Take an open seat. The host starts the game once everyone is in.");
  }

  private renderControls(v: TableView, mine: PlayerView): void {
    const legal = v.legal!;
    const cfg = this.config!;

    const fold = el<HTMLButtonElement>("btn fold", "Fold", "button");
    fold.onclick = () => this.send({ t: "act", action: { type: "fold" } });

    const call = el<HTMLButtonElement>("btn call", legal.call ? `Call ${money(legal.call)}` : "Check", "button");
    call.onclick = () => this.send({ t: "act", action: legal.call ? { type: "call" } : { type: "check" } });
    this.bar.append(fold, call);

    if (!legal.raise) return;
    const { min, max } = legal.raise;

    const key = `${v.handNumber}:${v.street}:${v.currentBet}:${min}:${max}`;
    if (key !== this.raiseKey) {
      this.raiseKey = key;
      this.raiseTo = min;
    }

    const toCall = legal.call ?? 0;
    const unit = cfg.unit;
    const clamp = (x: number) => Math.min(max, Math.max(min, Math.round(x / unit) * unit));
    const potRaise = (frac: number) => clamp(v.currentBet + frac * (v.pot + toCall));

    const panel = el("raise-panel");
    const raise = el<HTMLButtonElement>("btn raise", "", "button");

    const label = () => {
      const to = this.raiseTo;
      raise.textContent = to >= max ? `All-in ${money(to)}` : v.currentBet === 0 ? `Bet ${money(to)}` : `Raise to ${money(to)}`;
    };
    const set = (to: number) => {
      this.raiseTo = clamp(to);
      label();
    };

    // Row 1: jump to a size. Row 2: add chips to whatever is currently selected.
    const jumps: Array<[string, () => number]> = [
      ["Min", () => min],
      ["½ pot", () => potRaise(0.5)],
      ["Pot", () => potRaise(1)],
      ["All in", () => max],
    ];
    const adds: Array<[string, number]> = [["+$1", 100], ["+$5", 500], ["+$10", 1000], ["+$25", 2500]];

    const jumpRow = el("quick");
    for (const [text, to] of jumps) {
      const b = el<HTMLButtonElement>("", text, "button");
      b.onclick = () => set(to());
      jumpRow.append(b);
    }
    const addRow = el("quick adds");
    for (const [text, amount] of adds) {
      const b = el<HTMLButtonElement>("", text, "button");
      b.onclick = () => set(this.raiseTo + amount);
      addRow.append(b);
    }
    raise.onclick = () =>
      this.send({ t: "act", action: this.raiseTo >= max ? { type: "allin" } : { type: "raise", amount: this.raiseTo } });
    label();

    panel.append(jumpRow, addRow);
    this.bar.append(panel, raise);
    void mine;
  }

  private renderCorner(): void {
    const mine = this.mine();
    if (mine) {
      const leave = el<HTMLButtonElement>("btn ghost", "Leave table", "button");
      leave.onclick = () => this.send({ t: "leave" });
      this.corner.append(leave);
    }
    if (this.view?.hostId === this.identity.playerId && this.view.started) {
      const settings = el<HTMLButtonElement>("btn ghost", "Table settings", "button");
      settings.onclick = () => this.openSettings();
      this.corner.append(settings);
    }
    if (this.allowBots) {
      const add = el<HTMLButtonElement>("btn ghost", "Add bot", "button");
      add.onclick = () => this.send({ t: "addBot" });
      const rm = el<HTMLButtonElement>("btn ghost", "Remove bots", "button");
      rm.onclick = () => this.send({ t: "removeBots" });
      this.corner.append(add, rm);
    }
  }

  // ================================================================= modal

  /** Sitting down: the buy-in is the same for everyone and set by the host, so this is just a confirmation. */
  private openBuyIn(seat: number): void {
    const cfg = this.config;
    if (!cfg) return;
    const amount = cfg.maxBuyIn;
    this.modal.innerHTML = "";
    const box = el(
      "modal",
      `<h2>Take a seat</h2><p>Play money only. Everyone at this table buys in for the same amount.</p>` +
        `<div class="big money">${money(amount)}</div><div class="row"></div>`,
    );
    const cancel = el<HTMLButtonElement>("btn ghost", "Cancel", "button");
    cancel.onclick = () => this.modal.classList.remove("on");
    const ok = el<HTMLButtonElement>("btn solid", "Sit down", "button");
    ok.onclick = () => {
      this.modal.classList.remove("on");
      this.send({ t: "sit", buyIn: amount, seat });
    };
    box.querySelector(".row")!.append(cancel, ok);
    this.modal.append(box);
    this.modal.classList.add("on");
  }

  /** Host only. Before the game starts: pick the blinds and the buy-in. After it starts the blinds are locked. */
  private openSettings(): void {
    const cfg = this.config;
    if (!cfg) return;
    const locked = !!this.view?.started;
    const unit = cfg.unit;
    const lo = (bb: number) => bb * 20;
    const hi = (bb: number) => Math.min(bb * 10_000, 10_000_000);

    this.modal.innerHTML = "";
    const note = locked
      ? `The blinds are locked for this game. The buy-in applies to new seats and rebuys; players already seated keep their chips.`
      : `Choose the blinds carefully: they are locked as soon as you start the game. Everyone sitting now gets the buy-in as their starting stack.`;
    const box = el(
      "modal wide",
      `<h2>Table settings</h2><p>${note}</p>` +
        `<div class="sect">Blinds${locked ? " (locked)" : ""}</div><div class="presets blind-levels"></div>` +
        `<div class="pair">` +
        `<label class="field"><span>Small</span><b>$</b><input class="sb" type="number" inputmode="decimal" step="${unit / 100}" min="${unit / 100}" /></label>` +
        `<label class="field"><span>Big</span><b>$</b><input class="bb" type="number" inputmode="decimal" step="${unit / 100}" min="${unit / 100}" /></label>` +
        `</div>` +
        `<div class="sect">Buy-in</div><div class="presets buyin-levels"></div>` +
        `<label class="field"><span>Everyone buys in for</span><b>$</b><input class="bi" type="number" inputmode="decimal" step="${unit / 100}" min="${unit / 100}" /></label>` +
        `<div class="err"></div><div class="row"></div>`,
    );
    if (locked) box.classList.add("blinds-locked");
    const sb = box.querySelector<HTMLInputElement>(".sb")!;
    const bb = box.querySelector<HTMLInputElement>(".bb")!;
    const bi = box.querySelector<HTMLInputElement>(".bi")!;
    const err = box.querySelector<HTMLElement>(".err")!;
    const cents = (i: HTMLInputElement) => Math.round(Number(i.value) * 100);
    sb.value = String(cfg.smallBlind / 100);
    bb.value = String(cfg.bigBlind / 100);
    bi.value = String(cfg.maxBuyIn / 100);
    sb.disabled = bb.disabled = locked;

    // Buy-in presets are multiples of the big blind (so they always make sense at these stakes).
    const buyinBox = box.querySelector(".presets.buyin-levels")!;
    const drawBuyIns = () => {
      buyinBox.innerHTML = "";
      const big = cents(bb);
      if (!(big > 0)) return;
      for (const mult of [20, 50, 100, 200, 500]) {
        const amount = big * mult;
        if (amount < lo(big) || amount > hi(big)) continue;
        const b = el<HTMLButtonElement>("", moneyShort(amount), "button");
        b.title = `${mult} big blinds`;
        b.onclick = () => { bi.value = String(amount / 100); err.textContent = ""; };
        buyinBox.append(b);
      }
    };
    drawBuyIns();
    bb.oninput = () => { drawBuyIns(); err.textContent = ""; };

    const blindsBox = box.querySelector(".presets.blind-levels")!;
    const levels: Array<[number, number]> = [[25, 50], [50, 100], [100, 200], [200, 400], [500, 1000], [1000, 2000]];
    for (const [s1, b1] of levels) {
      const b = el<HTMLButtonElement>("", `${moneyShort(s1)} / ${moneyShort(b1)}`, "button");
      b.disabled = locked;
      b.onclick = () => {
        sb.value = String(s1 / 100);
        bb.value = String(b1 / 100);
        // keep the buy-in sensible: if it no longer fits these blinds, snap to 100 big blinds
        const cur = cents(bi);
        if (!(cur >= lo(b1) && cur <= hi(b1))) bi.value = String(Math.min(b1 * 100, hi(b1)) / 100);
        drawBuyIns();
        err.textContent = "";
      };
      blindsBox.append(b);
    }

    const cancel = el<HTMLButtonElement>("btn ghost", "Cancel", "button");
    cancel.onclick = () => this.modal.classList.remove("on");
    const save = el<HTMLButtonElement>("btn solid", "Save", "button");
    save.onclick = () => {
      const small = cents(sb), big = cents(bb), buyIn = cents(bi);
      const bad = [small, big, buyIn].some((v) => !Number.isFinite(v) || v <= 0 || v % unit !== 0);
      if (bad) { err.textContent = `Use amounts that are a multiple of ${money(unit)}.`; return; }
      if (big < small) { err.textContent = "The big blind can't be smaller than the small blind."; return; }
      if (big > 100_000) { err.textContent = `The big blind can be at most ${money(100_000)}.`; return; }
      if (buyIn < lo(big) || buyIn > hi(big)) {
        err.textContent = `With a ${money(big)} big blind, the buy-in must be ${money(lo(big))} to ${money(hi(big))}.`;
        return;
      }
      this.modal.classList.remove("on");
      this.send({ t: "setSettings", smallBlind: small, bigBlind: big, buyIn });
    };
    box.querySelector(".row")!.append(cancel, save);
    this.modal.append(box);
    this.modal.classList.add("on");
  }

  private toast(text: string): void {
    this.toastEl.textContent = text;
    this.toastEl.classList.add("on");
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toastEl.classList.remove("on"), 3200);
  }

  // ============================================================== animation

  private sleep(ms: number, fast: boolean): Promise<void> {
    return fast ? Promise.resolve() : new Promise((r) => setTimeout(r, ms));
  }

  private say(text: string, ms = 2800): void {
    this.speechEl.textContent = text;
    this.speechEl.classList.add("on");
    this.dealerBack.classList.add("talking");
    clearTimeout(this.speakTimer);
    this.speakTimer = window.setTimeout(() => {
      this.speechEl.classList.remove("on");
      this.dealerBack.classList.remove("talking");
    }, ms);
    window.setTimeout(() => this.dealerBack.classList.remove("talking"), Math.min(ms, 1400));
  }

  /** One hand flicks out from the dealer. */
  private flick(): { x: number; y: number } {
    const side = this.flickSide++ % 2;
    const arm = this.dealerEl.querySelector<SVGElement>(side === 0 ? ".d-arm-l" : ".d-arm-r")!;
    const deg = side === 0 ? -24 : 24;
    arm.animate([{ transform: "rotate(0deg)" }, { transform: `rotate(${deg}deg)`, offset: 0.4 }, { transform: "rotate(0deg)" }], { duration: 320, easing: "ease-out" });
    return DEALER_HANDS[side]!;
  }

  /** A face-down card flies from the dealer's hand to `target`. Resolves when it lands. */
  private async fly(from: { x: number; y: number }, target: HTMLElement, fast: boolean): Promise<void> {
    if (fast) return;
    const to = this.pos(target);
    const w = target.offsetWidth;
    const h = target.offsetHeight;
    const c = makeCard();
    c.classList.add("fly");
    c.style.width = `${w}px`;
    c.style.height = `${h}px`;
    c.style.left = `${to.x - w / 2}px`;
    c.style.top = `${to.y - h / 2}px`;
    this.stage.append(c);
    const anim = c.animate(
      [
        { transform: `translate(${from.x - to.x}px, ${from.y - to.y}px) scale(.55) rotate(-25deg)`, opacity: 0.2 },
        { transform: "translate(0,0) scale(1) rotate(0deg)", opacity: 1 },
      ],
      { duration: 420, easing: "cubic-bezier(.2,.7,.2,1)", fill: "forwards" },
    );
    await anim.finished.catch(() => undefined);
    c.remove();
  }

  private async dealHole(v: TableView, fast: boolean): Promise<void> {
    const order: number[] = [];
    for (let k = 1; k <= N; k++) {
      const s = (v.button + k) % N;
      if (v.players[s]?.hole.length === 2) order.push(s);
    }
    if (fast) {
      this.holeShown = Array.from({ length: N }, (_, s) => (order.includes(s) ? [true, true] : [false, false]));
      this.renderCards();
      return;
    }
    const jobs: Promise<void>[] = [];
    let n = 0;
    for (let pass = 0; pass < 2; pass++) {
      for (const s of order) {
        const delay = n++ * 120;
        jobs.push(
          (async () => {
            await this.sleep(delay, false);
            const from = this.flick();
            await this.fly(from, this.seats[s]!.cards[pass]!, false);
            this.holeShown[s]![pass] = true;
            this.renderCards();
          })(),
        );
      }
    }
    await Promise.all(jobs);
  }

  private async dealBoard(cards: Card[], fast: boolean): Promise<void> {
    for (const _ of cards) {
      const idx = this.boardShown;
      if (!fast) {
        const from = this.flick();
        await this.fly(from, this.boardCards[idx]!, false);
      }
      this.boardShown = idx + 1;
      this.renderBoard();
      await this.sleep(fast ? 0 : 230, fast);
    }
  }

  /** Chips slide from a bet spot into the pot. */
  private sweep(betEl: HTMLElement, html: string): void {
    if (this.reduced) return;
    const ghost = el("bet", html);
    ghost.style.left = betEl.style.left;
    ghost.style.top = betEl.style.top;
    ghost.style.zIndex = "30";
    this.stage.append(ghost);
    const dx = POT_POS.x - parseFloat(betEl.style.left);
    const dy = POT_POS.y - parseFloat(betEl.style.top);
    const a = ghost.animate(
      [{ transform: "translate(-50%,-50%)", opacity: 1 }, { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(.8)`, opacity: 0 }],
      { duration: 520, easing: "ease-in", fill: "forwards" },
    );
    a.finished.then(() => ghost.remove(), () => ghost.remove());
  }

  private async awardPot(seat: number, amount: Cents, hand: string | undefined, fast: boolean): Promise<void> {
    const p = this.seats[seat]!;
    const prev = this.winners.get(seat);
    this.winners.set(seat, { amount: (prev?.amount ?? 0) + amount, hand: hand ?? prev?.hand });
    this.renderBadges();
    const name = this.names.get(this.view!.players[seat]?.id ?? "") ?? "Someone";
    const slotPos = this.pos(p.avatar);

    if (!fast && !this.reduced) {
      const colors = ["green", "red", "white", "blue", "yellow"];
      for (let i = 0; i < 7; i++) {
        const chip = el(`fly-chip ${colors[i % colors.length]}`);
        chip.style.left = `${POT_POS.x}px`;
        chip.style.top = `${POT_POS.y}px`;
        this.stage.append(chip);
        const a = chip.animate(
          [{ transform: "translate(0,0) scale(1)", opacity: 1 }, { transform: `translate(${slotPos.x - POT_POS.x}px, ${slotPos.y - POT_POS.y}px) scale(.7)`, opacity: 0.9 }],
          { duration: 650, delay: i * 70, easing: "cubic-bezier(.3,.6,.3,1)", fill: "both" },
        );
        a.finished.then(() => chip.remove(), () => chip.remove());
      }
      const f = el("float", `+${money(amount)}`);
      f.style.left = `${Math.min(W - 110, Math.max(110, slotPos.x))}px`;
      f.style.top = `${slotPos.y - 40}px`;
      this.stage.append(f);
      f.animate([{ transform: "translate(-50%,-50%)", opacity: 0 }, { transform: "translate(-50%,-90%)", opacity: 1, offset: 0.2 }, { transform: "translate(-50%,-190%)", opacity: 0 }], { duration: 1900, easing: "ease-out" })
        .finished.then(() => f.remove(), () => f.remove());
    }
    this.say(`${name} wins ${money(amount)}${hand ? ` with ${hand.toLowerCase()}` : ""}.`, 4200);
    await this.sleep(900, fast);
  }

  // ============================================================ event playback

  private async playEvent(ev: GameEvent, v: TableView, fast: boolean): Promise<void> {
    const name = (id: string) => this.names.get(id) ?? "Someone";
    switch (ev.type) {
      case "hand_started":
        this.say(`Hand ${ev.handNumber}. Good luck.`, 2200);
        await this.sleep(500, fast);
        break;
      case "deal_hole":
        this.say("Cards are out.", 2000);
        await this.dealHole(v, fast);
        break;
      case "deal_board": {
        this.lastAction.clear();
        this.renderBadges();
        this.say(ev.street === "flop" ? "Flop." : ev.street === "turn" ? "Turn." : "River.", 2000);
        await this.dealBoard(ev.cards, fast);
        await this.sleep(350, fast);
        break;
      }
      case "action": {
        const seat = this.seatById.get(ev.playerId);
        if (seat === undefined) break;
        const text =
          ev.action === "fold" ? "Fold"
          : ev.action === "check" ? "Check"
          : ev.action === "call" ? `Call ${moneyShort(ev.amount)}`
          : ev.action === "bet" ? `Bet ${moneyShort(ev.amount)}`
          : ev.action === "raise" ? `Raise to ${moneyShort(ev.amount)}`
          : "All-in";
        this.lastAction.set(seat, { text, kind: ev.action === "fold" ? "fold" : ev.action === "allin" ? "allin" : "act" });
        this.renderBadges();
        break;
      }
      case "showdown":
        this.revealHold = false;
        this.say("Showdown.", 2200);
        for (const r of ev.reveals) {
          const seat = this.seatById.get(r.playerId);
          if (seat !== undefined) this.handLabels.set(seat, r.hand);
        }
        this.renderCards();
        this.renderBadges();
        await this.sleep(1100, fast);
        break;
      case "pot_awarded": {
        const seat = this.seatById.get(ev.playerId);
        if (seat !== undefined) await this.awardPot(seat, ev.amount, ev.hand, fast);
        break;
      }
      case "settings_changed": {
        const parts: string[] = [];
        if (ev.blindsChanged) parts.push(`Blinds are ${moneyShort(ev.smallBlind)} / ${moneyShort(ev.bigBlind)}`);
        if (ev.buyInChanged) parts.push(`the buy-in is ${moneyShort(ev.buyIn)}`);
        const text = parts.join(", and ");
        if (text) this.say(`${text[0]!.toUpperCase()}${text.slice(1)}.`, 3600);
        break;
      }
      case "game_started":
        this.say(`The game is on. Blinds are ${moneyShort(ev.smallBlind)} / ${moneyShort(ev.bigBlind)}, and they stay that way.`, 4600);
        break;
      case "game_over":
        this.say("That's the game, boys. The table's closed.", 3600);
        break;
      case "uncalled_bet_returned":
        this.say(`${money(ev.amount)} goes back to ${name(ev.playerId)}.`, 2200);
        break;
      default:
        break;
    }
  }
}

void AVATAR_COLORS;
